// Auth-error handling for the PowerSync upload path (F2 / #808).
//
// A 401 `unauthenticated` during `uploadData` is its OWN class: the session
// cookie expired server-side. It is NEITHER of the two existing dispositions:
//   - NOT permanent (`isPermanentUploadError` returns false for it) — we must
//     never quarantine-drop a surveyor's count for an auth blip; and
//   - NOT a plain transient rethrow — a bare rethrow skips `batch.complete()`,
//     so the SDK retries the same batch every ~5 s FOREVER and the whole device
//     queue jams behind it (Sentry 1N; the 7,119-loss device). The sync-DOWN
//     path already re-auths on 401 (fetchCredentials → /auth/login); the
//     sync-UP path had no equivalent — this module is that equivalent.
//
// Disposition (the #806 product decision): treat 401 as its own class —
//   1. attempt a silent credential refresh (POST /auth/refresh, which also
//      SLIDES the cookie). If the session is still valid the upload 401 was a
//      transient blip → 'retry' (the caller rethrows so the SDK retries the
//      whole batch under the refreshed cookie; the backend is idempotent under
//      LWW, so the retry is safe);
//   2. if it cannot refresh (genuine expiry → 401), PAUSE uploads, surface a
//      recoverable re-auth (navigate to /auth/login), and leave the batch
//      UN-acked so the queue is PRESERVED in ps_crud.
//   3. if /auth/refresh ITSELF keeps failing transiently (5xx / network) we
//      cannot tell "session valid, refresh flaky" from "session gone, refresh
//      down" — so we retry, but only BOUNDED-many times: an unbounded retry on a
//      persistently-down /auth/refresh + an expired session is the very
//      infinite-rethrow wedge this fix forbids, so after
//      MAX_INDETERMINATE_REFRESHES consecutive indeterminate refreshes we
//      escalate to PAUSE (audit-1 #2).
//
// Never quarantine-drops, never silently loses a count, never leaves the queue
// jammed in an unbounded rethrow.
//
// The pause LATCH lives in the leaf `upload-pause-latch.ts` (imported here AND
// by session-refresh.ts) so `refreshSession` can be the SINGLE resume point on
// any /auth/refresh 2xx without a circular import — see that file.

import * as Sentry from '@sentry/react'

import { ApiError, navigateTo } from '../api'
import { refreshSession } from '../session-refresh'

import {
  areUploadsPausedForReauth,
  bumpIndeterminateRefreshRun,
  pauseUploadsForReauth,
  resetIndeterminateRefreshRun,
  resumeUploadsAfterReauth,
  _resetUploadAuthLatch,
} from './upload-pause-latch'

// Re-export the latch surface so existing importers (connector.ts and the test
// suites) keep their `from './connector-upload-auth'` import path unchanged.
export { areUploadsPausedForReauth, resumeUploadsAfterReauth }

/**
 * 'retry'  → the session is still valid (or /auth/refresh failed transiently
 *            and we are still within the bounded-indeterminate budget); the
 *            caller should rethrow so the SDK retries the whole batch.
 * 'paused' → the session is genuinely gone (or /auth/refresh has been down for
 *            MAX_INDETERMINATE_REFRESHES consecutive passes); uploads are paused
 *            and re-auth is surfaced. The caller returns WITHOUT acking (queue
 *            preserved) and WITHOUT rethrowing (no jam).
 */
export type UploadAuthOutcome = 'retry' | 'paused'

// Bounded-indeterminate budget (audit-1 #2). At ~5 s SDK retry cadence this
// caps the rethrow-wedge at ~25 s before we surface re-auth + pause rather than
// rethrow forever on a persistently-5xx /auth/refresh. The COUNT lives in the
// leaf (upload-pause-latch.ts) so a resume clears it alongside the latch
// (audit-2: a stuck counter would mis-escalate the next unrelated 401 episode).
const MAX_INDETERMINATE_REFRESHES = 5

/** True for an `ApiError` that means "not authenticated" (expired session). */
export function isUploadAuthError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.status === 401 || err.code === 'unauthenticated')
  )
}

/** Test seam — reset the module-level latch AND the indeterminate counter. */
export function _resetUploadAuthPause(): void {
  _resetUploadAuthLatch()
}

/**
 * Recover from an upload-path auth error (gate on `isUploadAuthError` first).
 * Attempts a silent credential refresh and decides retry-vs-pause. Never throws.
 */
export async function recoverUploadAuth(): Promise<UploadAuthOutcome> {
  let result: { ok: boolean }
  try {
    // refreshSession resumes the pause latch AND zeroes the indeterminate run on
    // a 2xx (the single convergent resume point — see upload-pause-latch.ts).
    result = await refreshSession()
  } catch {
    // /auth/refresh itself failed transiently (5xx / network) — indeterminate.
    // Retry, but only bounded-many times so a persistently-down refresh on an
    // expired session cannot wedge the queue forever (audit-1 #2).
    if (bumpIndeterminateRefreshRun() >= MAX_INDETERMINATE_REFRESHES) {
      pauseAndSurfaceReauth()
      return 'paused'
    }
    return 'retry'
  }
  // A definitive refresh outcome (valid or expired) ends the indeterminate run.
  resetIndeterminateRefreshRun()
  if (result.ok) {
    // Session still valid (the cookie slid forward; the latch was cleared inside
    // refreshSession). The upload 401 was a transient blip — let the caller
    // rethrow for a retry.
    return 'retry'
  }
  // Genuine expiry — pause + surface re-auth + preserve the queue.
  pauseAndSurfaceReauth()
  return 'paused'
}

/**
 * Pause uploads, surface a recoverable re-auth, and beacon ONCE per pause
 * episode (edge-triggered on the false→true latch transition). Navigates every
 * call, but the latch makes a later uploadData pass a no-op so this runs once.
 */
function pauseAndSurfaceReauth(): void {
  if (!areUploadsPausedForReauth()) {
    beaconReauthPause()
    pauseUploadsForReauth()
  }
  navigateTo('/auth/login')
}

/**
 * Edge-triggered, no-PII telemetry that uploads paused for re-auth. Best-effort
 * and synchronous — never throws, never blocks the pause. A SYNTHETIC error
 * (never a raw ApiError, whose `.message` is the backend string) keeps the
 * payload off Sentry; the `extra` is a hand-built non-PII allowlist
 * (`reason` · `paused`). Locked down by
 * scripts/lint-powersync-reauth-beacon-no-pii.mjs.
 */
function beaconReauthPause(): void {
  try {
    Sentry.captureException(new Error('powersync_upload_reauth_pause'), {
      level: 'warning',
      tags: { area: 'powersync_upload_reauth_pause' },
      extra: { reason: 'powersync_upload_reauth_pause', paused: true },
    })
    console.warn(
      '[powersync] upload paused — session expired; re-auth required ' +
        '(queue preserved, not dropped — F2 / issue 808)',
    )
  } catch {
    // Best-effort: telemetry must never replace the pause/preserve behaviour.
  }
}
